// src/components/document/EntityCombobox.tsx
// Searchable single-select combobox over a plain item list, with a manual
// free-text fallback for when the backing service has no suitable entity.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useI18n } from '@react/hooks';

export interface EntityComboboxItem {
  id: string;
  title: string;
  subtitle?: string;
}

interface EntityComboboxProps {
  id: string;
  label: string;
  placeholder: string;
  items: EntityComboboxItem[];
  loading?: boolean;
  value: EntityComboboxItem | null;
  onSelect: (item: EntityComboboxItem | null) => void;
  /** Manual free-text value (used when no entity fits). */
  manualText?: string;
  onManualTextChange?: (text: string) => void;
  emptyText: string;
  manualActionText: string;
  manualPlaceholder: string;
  manualHint?: string;
}

const inputCls =
  'w-full rounded-md border border-input bg-background px-3 py-1.5 text-[13px] text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring';

export function EntityCombobox({
  id,
  label,
  placeholder,
  items,
  loading,
  value,
  onSelect,
  manualText = '',
  onManualTextChange,
  emptyText,
  manualActionText,
  manualPlaceholder,
  manualHint,
}: EntityComboboxProps) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [manual, setManual] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDocClick = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const filtered = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return items;
    return items.filter(
      (item) => item.title.toLowerCase().includes(normalized) ||
        (item.subtitle ?? '').toLowerCase().includes(normalized),
    );
  }, [items, query]);

  const choose = (item: EntityComboboxItem) => {
    onSelect(item);
    setOpen(false);
    setQuery('');
    setActiveIndex(0);
  };

  if (manual) {
    return (
      <div>
        <label htmlFor={id} className="mb-1 block text-xs font-medium text-muted-foreground">
          {label}
        </label>
        <input
          id={id}
          type="text"
          value={manualText}
          onChange={(event) => onManualTextChange?.(event.target.value)}
          placeholder={manualPlaceholder}
          className={inputCls}
        />
        <div className="mt-1 flex items-center justify-between gap-2">
          {manualHint ? <p className="text-[11px] text-muted-foreground">{manualHint}</p> : <span />}
          <button
            type="button"
            onClick={() => setManual(false)}
            className="text-[11px] text-primary hover:underline"
          >
            {t('document.wizard.pickFromList')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div ref={rootRef} className="relative">
      <label htmlFor={id} className="mb-1 block text-xs font-medium text-muted-foreground">
        {label}
      </label>
      {value ? (
        <div className="flex items-center gap-2 rounded-md border border-border bg-background px-3 py-1.5">
          <div className="min-w-0 flex-1">
            <div className="truncate text-[13px] text-foreground">{value.title}</div>
            {value.subtitle ? (
              <div className="truncate text-[11px] text-muted-foreground">{value.subtitle}</div>
            ) : null}
          </div>
          <button
            type="button"
            onClick={() => {
              onSelect(null);
              setQuery('');
            }}
            aria-label={t('common.reset')}
            className="rounded p-0.5 text-muted-foreground transition-colors hover:text-foreground"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
      ) : (
        <>
          <input
            id={id}
            type="text"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setOpen(true);
              setActiveIndex(0);
            }}
            onFocus={() => setOpen(true)}
            onKeyDown={(event) => {
              if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                event.preventDefault();
                setOpen(true);
                if (filtered.length > 0) {
                  const delta = event.key === 'ArrowDown' ? 1 : -1;
                  setActiveIndex((current) => (current + delta + filtered.length) % filtered.length);
                }
              } else if (event.key === 'Enter' && open && filtered[activeIndex]) {
                event.preventDefault();
                choose(filtered[activeIndex]);
              } else if (event.key === 'Escape') {
                setOpen(false);
              }
            }}
            placeholder={placeholder}
            className={inputCls}
            role="combobox"
            aria-autocomplete="list"
            aria-expanded={open}
            aria-controls={`${id}-listbox`}
            aria-activedescendant={open && filtered[activeIndex] ? `${id}-option-${activeIndex}` : undefined}
          />
          {open && (
            <div
              id={`${id}-listbox`}
              role="listbox"
              aria-label={label}
              className="absolute z-20 mt-1 max-h-56 w-full overflow-y-auto rounded-md border border-border bg-popover"
            >
              {loading ? (
                <div className="px-3 py-2 text-xs text-muted-foreground">{t('common.loading')}</div>
              ) : filtered.length === 0 ? (
                <div className="px-3 py-2 text-xs text-muted-foreground">{emptyText}</div>
              ) : (
                <ul className="divide-y divide-border" role="presentation">
                  {filtered.map((item, index) => (
                    <li key={item.id} role="presentation">
                      <button
                        id={`${id}-option-${index}`}
                        type="button"
                        role="option"
                        aria-selected={index === activeIndex}
                        onMouseEnter={() => setActiveIndex(index)}
                        onClick={() => choose(item)}
                        className={`w-full px-3 py-2 text-left transition-colors ${index === activeIndex ? 'bg-muted/60' : ''}`}
                      >
                        <div className="truncate text-[13px] text-foreground">{item.title}</div>
                        {item.subtitle ? (
                          <div className="truncate text-[11px] text-muted-foreground">{item.subtitle}</div>
                        ) : null}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </>
      )}
      {!value && onManualTextChange && (
        <button
          type="button"
          onClick={() => setManual(true)}
          className="mt-1 text-[11px] text-primary hover:underline"
        >
          {manualActionText}
        </button>
      )}
    </div>
  );
}

export default EntityCombobox;
